import type { BillingInformation } from "@/interfaces/billingInformations";
import { CustomAxiosRequestConfig } from "@/interfaces/customAxiosRequestConfig";
import type { MutationParams } from "@/interfaces/mutationParams";
import axios from "@/lib/axios";
import { useUserBillingInformationStore } from "@/stores/userBillingInformation";
import { useMutation, useQuery } from "@tanstack/vue-query";
import { storeToRefs } from "pinia";
import { watch } from "vue";
import { ToastInterface, useToast } from "vue-toastification";

interface Props {
  params: Params;
  immediate?: boolean;
}

interface Params extends Omit<Partial<BillingInformation>, "id"> {
  id: string | number;
  includes?: string[];
}

export interface SaveUserBillingParams {
  userId: string | number;
  existingId?: string | number;
  // file mode
  file?: File;
  // manual mode
  rfc?: string;
  legalName?: string;
  taxSystemId?: string | number;
  zipcode?: string;
  state?: string;
  street?: string;
  number?: string;
  internalNumber?: string;
  neighborhood?: string;
  municipality?: string;
  city?: string;
}

const getUserBillingInformation = async (
  params: Params,
): Promise<BillingInformation | null> => {
  const { data } = await axios.get<BillingInformation>(
    `/api/users/${params.id}/billing-informations`,
    { errorHandling: false } as CustomAxiosRequestConfig,
  );

  return data ?? null;
};

const saveUserBillingInformation = async (
  params: SaveUserBillingParams,
  toast: ToastInterface,
) => {
  try {
    if (params.file) {
      const formData = new FormData();
      formData.append("file", params.file);

      if (params.existingId) {
        formData.append("_method", "put");
        const { data } = await axios.post<BillingInformation>(
          `/api/billing-informations/${params.existingId}`,
          formData,
        );
        toast.success("La información fiscal se ha actualizado correctamente");
        return data;
      } else {
        const { data } = await axios.post<BillingInformation>(
          `/api/users/${params.userId}/billing-informations`,
          formData,
        );
        toast.success("La información fiscal se ha creado correctamente");
        return data;
      }
    } else {
      const payload = {
        rfc: params.rfc,
        legalName: params.legalName,
        taxSystemId: params.taxSystemId,
        zipcode: params.zipcode,
        state: params.state,
        street: params.street,
        number: params.number,
        internalNumber: params.internalNumber,
        neighborhood: params.neighborhood,
        municipality: params.municipality,
        city: params.city,
      };

      if (params.existingId) {
        const { data } = await axios.put<BillingInformation>(
          `/api/billing-informations/${params.existingId}`,
          payload,
        );
        toast.success("La información fiscal se ha actualizado correctamente");
        return data;
      } else {
        const { data } = await axios.post<BillingInformation>(
          `/api/users/${params.userId}/billing-informations`,
          payload,
        );
        toast.success("La información fiscal se ha creado correctamente");
        return data;
      }
    }
  } catch (e: any) {
    toast.error(e.response?.data.message);
  }
};

const useUserBillingInformation = (props: Props) => {
  const { setBillingInformation } = useUserBillingInformationStore();
  const { billingInformation } = storeToRefs(useUserBillingInformationStore());

  const { immediate = false } = props;
  const toast = useToast();

  const { data, isLoading, isError, isFetching } = useQuery(
    ["userBillingInformation", props.params.id],
    () =>
      getUserBillingInformation({
        id: props.params.id,
      }),
    {
      retry: false,
      enabled: immediate,
      refetchOnWindowFocus: false,
    },
  );

  watch(data, () => {
    if (data.value) {
      setBillingInformation(data.value);
    }
  });

  const saveMutation = useMutation(
    (props: MutationParams<SaveUserBillingParams>) =>
      saveUserBillingInformation(props.params, toast),
  );

  return {
    data,
    isError,
    isLoading,
    isFetching,

    billingInformation,

    saveMutation,
    save: saveMutation.mutate,
    saveAsync: saveMutation.mutateAsync,
    saveLoading: saveMutation.isLoading,
  };
};

export default useUserBillingInformation;
